import { z } from 'zod';
/**
 * The complete predecessor key set a step's `inputs[]` names: every
 * `sourceSteps[].stepId` (predecessor step OR legacy start-event id) plus every
 * first-class `event` input's `eventId`. Ids are stringified so ObjectId and
 * hex carriers compare equal. Shared by the backend recording view and the
 * frontend draft editor so both number off the identical edge set.
 */ export function collectStepInputPredecessors(inputs) {
    const out = [];
    for (const input of inputs ?? []){
        if (input?.eventId != null) out.push(String(input.eventId));
        for (const src of input?.sourceSteps ?? []){
            if (src?.stepId != null) out.push(String(src.stepId));
        }
    }
    return out;
}
/**
 * Runtime mirror of {@link StepNumberBridge} for the `ctx.data` seam. Annotated
 * with the interface rather than left to inference, so the two cannot drift:
 * a field added to one and not the other stops compiling here.
 */ export const StepNumberBridgeSchema = z.object({
    stableStepId: z.string(),
    prevKeys: z.array(z.string())
});
/**
 * Build the bridge a placeholder contributes to the walk, from the predecessor
 * keys its inputs resolved to.
 *
 * A placeholder whose inputs name no upstream source at all sits at the process
 * entry, so it anchors at the legacy `'START'` root: leaving `prevKeys` empty
 * would instead detach it, stranding every step behind it in the orphan tail.
 * Shared by the published recording view and the draft editor canvas — the same
 * placeholder must produce the same numbers before and after publish, so this
 * rule cannot live in two hand-copied places.
 */ export function toStepNumberBridge(stableStepId, prevKeys) {
    return {
        stableStepId,
        prevKeys: prevKeys.length > 0 ? prevKeys : [
            'START'
        ]
    };
}
// Code-point comparison — deterministic across Node and every browser locale,
// unlike `localeCompare`. The backend recording view and the draft editor must
// agree on same-depth sibling order, so the tiebreak cannot depend on locale.
function compareIds(a, b) {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
}
/**
 * Returns a map of `id → 1-based step number` covering every entry in `steps`.
 * Bridges are never present in the result.
 */ export function assignStepNumbers({ steps, startKeys = [], bridges = [] }) {
    // Reverse adjacency: prevKey → nodes that name it as their predecessor.
    const childrenByPrev = new Map();
    const push = (prevKey, node)=>{
        const list = childrenByPrev.get(prevKey);
        if (list) list.push(node);
        else childrenByPrev.set(prevKey, [
            node
        ]);
    };
    for (const step of steps){
        for (const prevKey of step.predecessors)push(prevKey, {
            kind: 'step',
            id: step.id
        });
    }
    for (const bridge of bridges){
        for (const prevKey of bridge.prevKeys)push(prevKey, {
            kind: 'bridge',
            id: bridge.stableStepId
        });
    }
    // Layered BFS from the start anchors. Each step's depth is its layer on first
    // visit; bridge-only layers pass through (they create harmless gaps in the
    // depth values, which the final re-rank collapses).
    const depthById = new Map();
    const visitedBridges = new Set();
    // Visit one node: record its depth (steps) or mark it seen (bridges), then
    // queue its children. Returns early when already visited so a cyclic
    // reassignment chain can't loop forever.
    const visit = (node, depth, next)=>{
        if (node.kind === 'step') {
            if (depthById.has(node.id)) return;
            depthById.set(node.id, depth);
        } else {
            if (visitedBridges.has(node.id)) return;
            visitedBridges.add(node.id);
        }
        next.push(...childrenByPrev.get(node.id) ?? []);
    };
    let current = [
        'START',
        ...startKeys
    ].flatMap((root)=>childrenByPrev.get(root) ?? []);
    let depth = 1;
    while(current.length > 0){
        const next = [];
        for (const node of current)visit(node, depth, next);
        current = next;
        depth++;
    }
    // Orphans (steps not connected to any root) sort after everything else.
    const orphanDepth = depth;
    for (const step of steps){
        if (!depthById.has(step.id)) depthById.set(step.id, orphanDepth);
    }
    // Re-rank to a dense 1…N ordered by (depth, id). The id tiebreak is stable
    // and layout-free so draft and recording agree.
    const ordered = steps.map((step)=>({
            id: step.id,
            depth: depthById.get(step.id) ?? orphanDepth
        })).sort((a, b)=>a.depth - b.depth || compareIds(a.id, b.id));
    const result = new Map();
    ordered.forEach((entry, index)=>result.set(entry.id, index + 1));
    return result;
}
/**
 * Number process step recordings 1…N in flow order and emit them ascending, so
 * every display agrees on which step is "Schritt N".
 *
 * Pure by design: each caller loads the three ingredients its own way —
 * `show-process` from the event nodes and open assignments it already holds, an
 * app from `data.processSteps.listStartEvents` +
 * `.listOpenAssignmentBridges` — and the numbering they get back is identical.
 * A tool ordinal that diverges from the UI badge is worse than none, which is
 * why this lives beside {@link assignStepNumbers} instead of in the platform:
 * core and app import ONE implementation.
 *
 * @see assignStepNumbers — the BFS-depth algorithm and its `stableStepId` tiebreak
 * @see collectStepInputPredecessors — why the COMPLETE predecessor set, not the
 *   `previousStep` scalar (which strands a first step that also receives
 *   rejection loop-backs)
 * @see toStepNumberBridge — how an unrecorded placeholder joins the walk
 */ export function orderStepsByFlow({ steps, startKeys, bridges }) {
    const keyedSteps = steps.map((step)=>({
            step,
            id: step._id.toString()
        }));
    const numbersById = assignStepNumbers({
        steps: keyedSteps.map(({ step, id })=>({
                id,
                predecessors: collectStepInputPredecessors(step.inputs)
            })),
        startKeys,
        bridges
    });
    return keyedSteps.map(({ step, id })=>{
        const stepNumber = numbersById.get(id);
        // `assignStepNumbers` is total over the steps it was handed (a dense
        // 1…N, orphans included). A miss can only mean that stopped being true —
        // and a defaulted 0 would sort ahead of step 1 and be read out as an
        // ordinal, which is exactly the divergence this seam exists to prevent.
        if (stepNumber === undefined) {
            throw new Error(`orderStepsByFlow: step ${id} was left unnumbered`);
        }
        return {
            step,
            stepNumber
        };
    }).sort((a, b)=>a.stepNumber - b.stepNumber);
}

//# sourceMappingURL=step-numbering.js.map